import React, { useState } from 'react';
import { ClipboardCheck, Filter, Search, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import apiService from '../services/api';
import { format, parseISO } from 'date-fns';

const Audit: React.FC = () => {
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const { data, loading, error } = useApi(
    () => apiService.getAuditLogs({
      entity_type: entityTypeFilter === 'all' ? undefined : entityTypeFilter,
      page: currentPage,
      per_page: 20
    }),
    [entityTypeFilter, currentPage]
  );

  const getActionColor = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create':
        return 'text-green-400';
      case 'update':
        return 'text-yellow-400';
      case 'delete':
        return 'text-red-400';
      case 'read':
        return 'text-blue-400';
      default:
        return 'text-gray-400';
    }
  };

  const getActionBadge = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create':
        return 'badge-active';
      case 'update':
        return 'badge-pending';
      case 'delete':
        return 'badge-expired';
      default:
        return 'badge-inactive';
    }
  };

  const filteredLogs = data?.data.filter((log) =>
    `${log.user_id} ${log.action} ${log.entity_type}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const entityTypes = Array.from(new Set(data?.data.map((log) => log.entity_type) || []));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Audit Log</h1>
          <p className="mt-1 text-sm text-gray-400">
            Track all system changes and user activities
          </p>
        </div>
        <button
          onClick={() => {
            // Export audit logs as JSON
            if (data?.data) {
              const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }
          }}
          disabled={!data?.data || data.data.length === 0}
          className="btn-primary flex items-center gap-2 disabled:opacity-50"
        >
          <Download className="h-5 w-5" />
          Export Logs
        </button>
      </div>

      {/* Filters */}
      <div className="glass-card p-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search audit logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          <div className="flex items-center gap-3">
            <select
              value={entityTypeFilter}
              onChange={(e) => setEntityTypeFilter(e.target.value)}
              className="input-field w-48"
            >
              <option value="all">All Entity Types</option>
              {entityTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <button
              onClick={() => alert('Advanced filters coming soon')}
              className="btn-secondary flex items-center gap-2"
            >
              <Filter className="h-5 w-5" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="glass-card p-4">
          <p className="text-sm text-gray-400">Total Events</p>
          <p className="mt-1 text-2xl font-bold text-white">{data?.total || 0}</p>
        </div>
        <div className="glass-card p-4">
          <p className="text-sm text-gray-400">Creates</p>
          <p className="mt-1 text-2xl font-bold text-green-400">
            {data?.data.filter((log) => log.action.toLowerCase() === 'create').length || 0}
          </p>
        </div>
        <div className="glass-card p-4">
          <p className="text-sm text-gray-400">Updates</p>
          <p className="mt-1 text-2xl font-bold text-yellow-400">
            {data?.data.filter((log) => log.action.toLowerCase() === 'update').length || 0}
          </p>
        </div>
        <div className="glass-card p-4">
          <p className="text-sm text-gray-400">Deletes</p>
          <p className="mt-1 text-2xl font-bold text-red-400">
            {data?.data.filter((log) => log.action.toLowerCase() === 'delete').length || 0}
          </p>
        </div>
      </div>

      {/* Audit Logs Table */}
      {loading ? (
        <div className="flex h-96 items-center justify-center">
          <div className="spinner"></div>
        </div>
      ) : error ? (
        <div className="glass-card p-6 text-center text-red-400">{error.message}</div>
      ) : (
        <div className="space-y-4">
          {filteredLogs?.map((log) => (
            <div key={log.id} className="glass-card p-4 transition-all hover:border-sky-500/30">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <ClipboardCheck className="h-5 w-5 text-sky-400" />
                    <span className={`font-medium ${getActionColor(log.action)}`}>
                      {log.action.toUpperCase()}
                    </span>
                    <span className="badge-sky">{log.entity_type}</span>
                    <span className="text-sm text-gray-400">ID: {log.entity_id}</span>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                      <p className="text-xs text-gray-400">User</p>
                      <p className="mt-1 text-sm text-white">{log.user_id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Timestamp</p>
                      <p className="mt-1 text-sm text-white">
                        {format(parseISO(log.timestamp), 'MMM dd, yyyy HH:mm:ss')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">IP Address</p>
                      <p className="mt-1 text-sm text-white">{log.ip_address || 'N/A'}</p>
                    </div>
                  </div>

                  {log.changes && Object.keys(log.changes).length > 0 && (
                    <div className="mt-4 rounded-lg border border-gray-800 bg-gray-900/30 p-3">
                      <p className="mb-2 text-xs font-medium text-gray-400">Changes</p>
                      <div className="space-y-2">
                        {Object.entries(log.changes).map(([key, value]) => (
                          <div key={key} className="flex items-start gap-2 text-sm">
                            <span className="font-mono text-sky-400">{key}:</span>
                            <span className="text-gray-300">{JSON.stringify(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <span className={getActionBadge(log.action)}>{log.action}</span>
              </div>
            </div>
          ))}

          {filteredLogs?.length === 0 && (
            <div className="glass-card p-12 text-center">
              <p className="text-gray-400">No audit logs found</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {data && data.total_pages > 1 && (
        <div className="flex items-center justify-between glass-card p-4">
          <p className="text-sm text-gray-400">
            Page {data.page} of {data.total_pages} ({data.total} total results)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn-secondary px-3 py-1 text-sm flex items-center gap-1 disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(data.total_pages, currentPage + 1))}
              disabled={currentPage >= data.total_pages}
              className="btn-secondary px-3 py-1 text-sm flex items-center gap-1 disabled:opacity-50"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Audit;
